import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Check, Close, InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { useApplyToursPricingSuggestion } from '~/hooks/apis/lere/useApplyToursPricingSuggestion';
import { useGetLereToursPricingSuggestions } from '~/hooks/apis/lere/useGetLereToursPricingSuggestions';
import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import ToursPricingApplyDialog from './ToursPricingApplyDialog';
import ToursCapacitySalesFormatter from './formatters/ToursCapacitySalesFormatter';
import ToursCurrentPricingFormatter from './formatters/ToursCurrentPricingFormatter';
import ToursIdsFormatter from './formatters/ToursIdsFormatter';
import ToursPricingFormatter from './formatters/ToursPricingFormatter';
import ToursScheduleFormatter from './formatters/ToursScheduleFormatter';
import {
  PRICE_DIRECTION_OPTIONS,
  formatDate,
  getChangeColor,
  getPriceDirection,
  getPriceDirectionLabel,
  getTourOfferHref,
} from './formatters/toursPricingUtils';
import {
  buildAppliedPreview,
  buildApplyRequest,
  getPercentInputValue,
  validateAppliedPercentInput,
} from './toursPricingApply';
import { TOURS_PRICING_SAMPLE_DATA } from './toursPricingSampleData';
import {
  LerePaginatedResponse,
  LereTabComponentProps,
  LereToursPricingSuggestion,
  LereToursPricingSuggestionsParams,
  PriceDirection,
  ToursPricingApplyStatus,
} from './types';

const EMPTY_RESPONSE: LerePaginatedResponse<LereToursPricingSuggestion> = {
  result: [],
  total: 0,
};

type AppliedFilters = Omit<LereToursPricingSuggestionsParams, 'page' | 'limit'>;
const ACTIONS_COLUMN_WIDTH = 90;
const OFFER_NAME_COLUMN_WIDTH = 220;
const DEPARTURE_DATE_COLUMN_WIDTH = 135;
const PROPOSED_CHANGE_TOOLTIP =
  'Shows the model-suggested price change and compares it to the current live price. Applying this change replaces any existing surcharge/discount, and the final live price is recalculated from base price + applied change.';
const CAPACITY_SALES_TOOLTIP =
  'Dep values are for the whole departure: all packages sharing the seats, including LTLE. Package values are this package only. Tour values cover all future dates of the tour.';

export default function RMToursPricingSuggestions({ onLereError }: LereTabComponentProps) {
  const { paginationModel, setPaginationModel, setPage } = useDataGridPagination({ page: 0, pageSize: 20 });
  const {
    mutate: applyPricingSuggestion,
    isPending: isApplying,
    variables: applyingVariables,
  } = useApplyToursPricingSuggestion();

  const [offerBkInput, setOfferBkInput] = useState('');
  const [salesforceIdInput, setSalesforceIdInput] = useState('');
  const [priceDirectionInput, setPriceDirectionInput] = useState<PriceDirection | ''>('');
  const [useLiveData, setUseLiveData] = useState(true);
  const [applyDialogRow, setApplyDialogRow] = useState<LereToursPricingSuggestion | null>(null);
  const [appliedPercentInput, setAppliedPercentInput] = useState('');
  const [notesBySuggestionId, setNotesBySuggestionId] = useState<Record<string, string>>({});

  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({});

  const queryParams = useMemo<LereToursPricingSuggestionsParams>(
    () => ({
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      ...appliedFilters,
    }),
    [paginationModel.page, paginationModel.pageSize, appliedFilters],
  );

  const toursPricingSuggestionsQuery = useGetLereToursPricingSuggestions(queryParams, { enabled: useLiveData });

  const samplePaginatedData = useMemo<LerePaginatedResponse<LereToursPricingSuggestion>>(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    const end = start + paginationModel.pageSize;
    return {
      result: TOURS_PRICING_SAMPLE_DATA.slice(start, end),
      total: TOURS_PRICING_SAMPLE_DATA.length,
    };
  }, [paginationModel.page, paginationModel.pageSize]);

  const toursPricingSuggestionsData = useLiveData
    ? (toursPricingSuggestionsQuery.data ?? EMPTY_RESPONSE)
    : samplePaginatedData;

  const rowsWithNotes = useMemo(
    () =>
      toursPricingSuggestionsData.result.map((row) => ({
        ...row,
        note: notesBySuggestionId[row.suggestion_id] ?? row.note ?? '',
      })),
    [notesBySuggestionId, toursPricingSuggestionsData.result],
  );

  useEffect(() => {
    if (toursPricingSuggestionsQuery.error) {
      onLereError(toursPricingSuggestionsQuery.error);
    }
  }, [toursPricingSuggestionsQuery.error, onLereError]);

  const handleSearch = () => {
    setAppliedFilters({
      offer_bk: offerBkInput || undefined,
      offer_salesforce_bk: salesforceIdInput || undefined,
      price_direction: priceDirectionInput || undefined,
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setOfferBkInput('');
    setSalesforceIdInput('');
    setPriceDirectionInput('');
    setAppliedFilters({});
    setPage(0);
  };

  const handleActionTypeChange = (event: SelectChangeEvent) => {
    setPriceDirectionInput(event.target.value as PriceDirection | '');
  };

  const closeApplyDialog = useCallback(() => {
    setApplyDialogRow(null);
    setAppliedPercentInput('');
  }, []);

  const openApplyDialog = useCallback((row: LereToursPricingSuggestion) => {
    setApplyDialogRow(row);
    setAppliedPercentInput(getPercentInputValue(row.suggested_price_change_percent));
  }, []);

  const handleAction = useCallback(
    (row: LereToursPricingSuggestion, status: ToursPricingApplyStatus) => {
      if (status === 'accepted') {
        openApplyDialog(row);
        return;
      }

      applyPricingSuggestion(
        buildApplyRequest(row, status, undefined, notesBySuggestionId[row.suggestion_id] ?? row.note ?? undefined),
      );
    },
    [notesBySuggestionId, openApplyDialog, applyPricingSuggestion],
  );

  const appliedPercentValidation = useMemo(
    () => validateAppliedPercentInput(appliedPercentInput),
    [appliedPercentInput],
  );

  const handleApplyDialogConfirm = useCallback(() => {
    if (!applyDialogRow) {
      return;
    }

    const { value: appliedPriceChangePercent, error } = appliedPercentValidation;
    if (error || appliedPriceChangePercent == null) {
      return;
    }

    const appliedPreview = buildAppliedPreview(applyDialogRow, appliedPriceChangePercent);
    applyPricingSuggestion(
      buildApplyRequest(
        applyDialogRow,
        'accepted',
        appliedPreview,
        notesBySuggestionId[applyDialogRow.suggestion_id] ?? applyDialogRow.note ?? undefined,
      ),
      {
        onSuccess: closeApplyDialog,
      },
    );
  }, [applyDialogRow, appliedPercentValidation, applyPricingSuggestion, closeApplyDialog, notesBySuggestionId]);

  const pendingSuggestionId = isApplying ? applyingVariables?.suggestion_id : null;

  const columns = useMemo<Array<GridColDef<LereToursPricingSuggestion>>>(() => {
    const compactCol: Partial<GridColDef<LereToursPricingSuggestion>> = {
      minWidth: 120,
      flex: 1,
    };

    return [
      {
        field: 'actions',
        headerName: 'Actions',
        width: ACTIONS_COLUMN_WIDTH,
        sortable: false,
        filterable: false,
        headerClassName: 'sticky-actions-header',
        cellClassName: 'sticky-actions-cell',
        renderCell: ({ row }) => {
          if (pendingSuggestionId === row.suggestion_id) {
            return <CircularProgress size={24} />;
          }

          return (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ py: 1 }}>
              <Tooltip title="Accept & apply pricing change">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleAction(row, 'accepted')}
                  disabled={isApplying}
                >
                  <Check fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject recommendation">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleAction(row, 'rejected')}
                  disabled={isApplying}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
      {
        field: 'offer_name',
        headerName: 'Offer Name',
        width: OFFER_NAME_COLUMN_WIDTH,
        headerClassName: 'sticky-offer-name-header',
        cellClassName: 'sticky-offer-name-cell',
        renderCell: ({ row }) => {
          const name = row.offer_name || '—';
          const offerHref = getTourOfferHref(row.offer_bk, row.offer_url);
          if (offerHref && name !== '—') {
            return (
              <Link href={offerHref} target="_blank" rel="noopener noreferrer" underline="hover">
                {name}
              </Link>
            );
          }
          return name;
        },
      },
      {
        field: 'departure_date',
        headerName: 'Departure Date',
        width: DEPARTURE_DATE_COLUMN_WIDTH,
        headerClassName: 'sticky-departure-date-header',
        cellClassName: 'sticky-departure-date-cell',
        renderCell: ({ row }) => formatDate(row.departure_date),
      },
      {
        field: 'ids',
        headerName: 'IDs',
        display: 'flex',
        flex: 2,
        minWidth: 180,
        sortable: false,
        filterable: false,
        renderCell: ToursIdsFormatter,
      },
      {
        ...compactCol,
        field: 'price_direction',
        headerName: 'Action Type',
        minWidth: 205,
        renderCell: ({ row }) => (
          <Typography variant="body2" color={getChangeColor(getPriceDirection(row))}>
            {getPriceDirectionLabel(getPriceDirection(row))}
          </Typography>
        ),
      },
      {
        field: 'reason_headline',
        headerName: 'Reason',
        minWidth: 280,
        flex: 2,
        sortable: false,
        renderCell: ({ row }) =>
          row.reason_headline || row.reason_points?.length ? (
            <Box sx={{ py: 0.5, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
              {row.reason_headline && (
                <Typography variant="body2" fontWeight="bold">
                  {row.reason_headline}
                </Typography>
              )}
              {Boolean(row.reason_points?.length) && (
                <Box component="ul" sx={{ my: 0, pl: 2, typography: 'caption', lineHeight: 1.25 }}>
                  {row.reason_points?.map((point, index) => (
                    <Box component="li" key={`${index}-${point}`}>
                      {point}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ) : (
            '—'
          ),
      },
      {
        field: 'schedule',
        headerName: 'Schedule',
        display: 'flex',
        flex: 2,
        minWidth: 180,
        sortable: false,
        filterable: false,
        renderCell: ToursScheduleFormatter,
      },
      {
        field: 'current_pricing',
        headerName: 'Current Pricing',
        display: 'flex',
        flex: 2,
        minWidth: 220,
        sortable: false,
        filterable: false,
        renderCell: ToursCurrentPricingFormatter,
      },
      {
        field: 'proposed_change',
        headerName: 'Proposed Change',
        renderHeader: () => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <span>Proposed Change</span>
            <Tooltip title={PROPOSED_CHANGE_TOOLTIP} arrow placement="top">
              <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Stack>
        ),
        display: 'flex',
        flex: 2,
        minWidth: 280,
        sortable: false,
        filterable: false,
        renderCell: ToursPricingFormatter,
      },
      {
        field: 'capacity_sales',
        headerName: 'Capacity & Sales',
        renderHeader: () => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <span>Capacity & Sales</span>
            <Tooltip title={CAPACITY_SALES_TOOLTIP} arrow placement="top">
              <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Stack>
        ),
        display: 'flex',
        flex: 2,
        minWidth: 240,
        sortable: false,
        filterable: false,
        renderCell: ToursCapacitySalesFormatter,
      },
      {
        ...compactCol,
        field: 'offer_country_name',
        headerName: 'Country',
        renderCell: ({ row }) => row.offer_country_name || '—',
      },
      {
        field: 'note',
        headerName: 'Notes',
        minWidth: 220,
        flex: 1.5,
        editable: true,
        sortable: false,
        filterable: false,
      },
    ];
  }, [pendingSuggestionId, isApplying, handleAction]);

  return (
    <Box>
      <Typography variant="h2" fontSize="2em" fontWeight="medium">
        RM Tours Pricing Suggestions
      </Typography>
      <Typography variant="subtitle1" fontWeight="medium" sx={{ mt: 3 }}>
        Filters
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1, mb: 3 }}>
        <TextField
          label="Offer ID"
          size="small"
          disabled={!useLiveData}
          value={offerBkInput}
          onChange={(e) => setOfferBkInput(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <TextField
          label="Salesforce ID"
          size="small"
          disabled={!useLiveData}
          value={salesforceIdInput}
          onChange={(e) => setSalesforceIdInput(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }} disabled={!useLiveData}>
          <InputLabel>Action Type</InputLabel>
          <Select value={priceDirectionInput} label="Action Type" onChange={handleActionTypeChange}>
            <MenuItem value="">All</MenuItem>
            {PRICE_DIRECTION_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="text"
          disabled={!useLiveData || toursPricingSuggestionsQuery.isFetching}
          onClick={handleClearFilters}
        >
          Clear filters
        </Button>
        <Button
          variant="contained"
          disabled={!useLiveData || toursPricingSuggestionsQuery.isFetching}
          onClick={handleSearch}
        >
          Search
        </Button>
      </Stack>

      <DataGrid
        autoHeight
        columns={columns}
        rows={rowsWithNotes}
        rowCount={toursPricingSuggestionsData.total}
        loading={useLiveData && toursPricingSuggestionsQuery.isFetching}
        pagination
        paginationMode="server"
        pageSizeOptions={[10, 20, 50]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowHeight={() => 'auto'}
        getRowId={(row) => row.suggestion_id}
        slots={{
          pagination: GridPagination,
        }}
        processRowUpdate={(newRow) => {
          setNotesBySuggestionId((prev) => ({
            ...prev,
            [newRow.suggestion_id]: newRow.note ?? '',
          }));
          return newRow;
        }}
        disableRowSelectionOnClick
        disableColumnMenu
        sx={{
          width: '100%',
          '& .sticky-actions-header, & .sticky-actions-cell': {
            position: 'sticky',
            left: 0,
            overflow: 'visible',
            backgroundColor: 'background.paper',
          },
          '& .sticky-actions-header': {
            zIndex: 6,
          },
          '& .sticky-actions-cell': {
            zIndex: 5,
          },
          '& .sticky-offer-name-header, & .sticky-offer-name-cell': {
            position: 'sticky',
            left: ACTIONS_COLUMN_WIDTH,
            backgroundColor: 'background.paper',
          },
          '& .sticky-offer-name-header': {
            zIndex: 6,
          },
          '& .sticky-offer-name-cell': {
            zIndex: 5,
          },
          '& .sticky-departure-date-header, & .sticky-departure-date-cell': {
            position: 'sticky',
            left: ACTIONS_COLUMN_WIDTH + OFFER_NAME_COLUMN_WIDTH,
            backgroundColor: 'background.paper',
          },
          '& .sticky-departure-date-header': {
            zIndex: 6,
          },
          '& .sticky-departure-date-cell': {
            zIndex: 5,
            boxShadow: 1,
          },
          '& .MuiDataGrid-row': {
            cursor: 'default',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'transparent',
          },
          // Hide the resize separator on the sticky actions header.
          '& .sticky-actions-header .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
        }}
      />

      <ToursPricingApplyDialog
        applyDialogRow={applyDialogRow}
        closeApplyDialog={closeApplyDialog}
        appliedPercentInput={appliedPercentInput}
        setAppliedPercentInput={setAppliedPercentInput}
        isApplying={isApplying}
        handleApplyDialogConfirm={handleApplyDialogConfirm}
      />

      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Switch
              checked={useLiveData}
              onChange={(e) => {
                setUseLiveData(e.target.checked);
                setPage(0);
              }}
            />
          }
          label={
            <Typography variant="body2" color={useLiveData ? 'text.primary' : 'text.secondary'}>
              Use Live Data {!useLiveData && '(currently showing sample data)'}
            </Typography>
          }
        />
      </Box>
    </Box>
  );
}
